import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  TrendingDown,
  TrendingUp,
  Minus,
  Loader2,
  RefreshCw,
  ExternalLink,
  X,
  Video,
  Image as ImageIcon,
  Layers,
  Type,
  Trophy,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PLATFORM_CHART_COLOR, PLATFORM_LABEL, type SocialPlatform } from '@/client/types/marketing';
import { PlatformLogoBadge } from '@/client/pages/Admin/Media/components/PlatformLogo';
import { EmptyState, Panel } from './components/marketing-ui';
import {
  EMPTY_INSIGHT_FILTERS,
  type InsightFilters,
  OWNED_MAX_RANGE_DAYS,
  OWNED_RANGE_PRESETS,
  ownedDaysFromStart,
  ownedStartDateForDays,
  ownedInclusiveStart,
  ownedWindowBounds,
  bestTimes,
  changeVsPrior,
  er,
  formatChange,
  formatCompact,
  formatEr,
  hourLabel,
  HEAT_DAYS,
  HEAT_HOURS,
  sumMetrics,
  uniquePlatforms,
  bucketStart,
  bucketTick,
  type HeatCell,
  type TimeGrain,
} from './posting-insights-data';
import {
  hasAccountStep,
  bestCadence,
  cumulateEngagement,
  formatBreakdown,
  isDailyUnsynced,
  isUnsynced,
  liveContentDecay,
  liveDaily,
  liveEngagementOverTime,
  liveFollowers,
  liveHeat,
  livePostingFrequency,
  livePosts,
  livePublishedDaily,
} from './posting-insights-live';
import {
  loadOwnedRangePreference,
  saveOwnedRangePreference,
  servedWindowLabel,
  utcDayKey,
  utcDayTick,
} from './performance-range-storage';
import {
  usePerformanceOwnedQuery,
  useResyncOwnedMutation,
} from '@/client/api/marketing/radar';
import type { ApiOwnedMetricBag } from '@/client/types/marketing/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/client/utils/errors';
import Pagination from '@/client/components/common/Pagination';

/**
 * Window totals from the API -> the shape the page's renderers expect.
 *
 * Zero-fill happens here and nowhere upstream: the API omits metric keys it has
 * no reading for, and these are all sums, where a missing addend and a zero
 * addend are genuinely the same thing.
 */
const bagToMetrics = (bag: ApiOwnedMetricBag | undefined) => ({
  likes: bag?.likes ?? 0,
  comments: bag?.comments ?? 0,
  shares: bag?.shares ?? 0,
  saves: bag?.saves ?? 0,
  clicks: bag?.clicks ?? 0,
  views: bag?.views ?? 0,
  follows: bag?.follows ?? 0,
  impressions: bag?.impressions ?? 0,
  reach: bag?.reach ?? 0,
});

const ENGAGEMENT_SERIES = [
  { key: 'likes', label: 'Likes', color: '#E1306C' },
  { key: 'comments', label: 'Comments', color: '#0ea5e9' },
  { key: 'shares', label: 'Shares', color: '#8b5cf6' },
  { key: 'saves', label: 'Saves', color: '#f59e0b' },
  { key: 'views', label: 'Views', color: '#10b981' },
  { key: 'impressions', label: 'Impressions', color: '#6366f1' },
  { key: 'reach', label: 'Reach', color: '#f97316' },
  { key: 'clicks', label: 'Clicks', color: '#64748b' },
] as const;

/** Large-magnitude series share the right axis so likes/comments stay readable. */
const RIGHT_AXIS = new Set<string>(['views', 'impressions', 'reach']);

/** Metrics the per-platform / over-time bar pair can be switched to. */
const BAR_METRICS = [
  'likes', 'comments', 'shares', 'saves', 'views', 'impressions', 'reach', 'clicks',
] as const;
type BarMetric = (typeof BAR_METRICS)[number];
const METRIC_LABEL: Record<BarMetric, string> = {
  likes: 'Likes', comments: 'Comments', shares: 'Shares', saves: 'Saves',
  views: 'Views', impressions: 'Impressions', reach: 'Reach', clicks: 'Clicks',
};

type SortKey =
  | 'publishedAt'
  | 'likes'
  | 'comments'
  | 'shares'
  | 'saves'
  | 'clicks'
  | 'views'
  | 'follows'
  | 'impressions'
  | 'reach'
  | 'er';

/** Client-side slice of the loaded top_posts list (not a full archive query). */
type PostDrill = {
  /** `bucketStart` key at the page grain. */
  period: string | null;
  focusPostId: string | null;
};

const EMPTY_DRILL: PostDrill = { period: null, focusPostId: null };

const GRAIN_LABEL: Record<TimeGrain, string> = { day: 'Day', week: 'Week', month: 'Month' };

/** Tooltip / drill text for a bucket key: "2026-10-26", "week of 2026-10-26", "Oct 26". */
const periodLabel = (start: string, grain: TimeGrain): string =>
  grain === 'day' ? start : grain === 'week' ? `week of ${start}` : bucketTick(start, grain);

const METRIC_CARD_SORT: Record<string, SortKey> = {
  Likes: 'likes',
  Comments: 'comments',
  Shares: 'shares',
  Saves: 'saves',
  Views: 'views',
  Impressions: 'impressions',
  Reach: 'reach',
  Clicks: 'clicks',
  'Eng. rate': 'er',
};

const KPI_SORT: Record<string, SortKey | 'followers'> = {
  'Engagement rate': 'er',
  'Total reach': 'reach',
  Followers: 'followers',
  Posts: 'publishedAt',
};

function chartRow<T extends Record<string, unknown>>(data: unknown): T | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as { payload?: T } & Partial<T>;
  return (d.payload ?? d) as T;
}

function Change({ change }: { change: number | null }) {
  const { label, tone } = formatChange(change);
  if (tone === 'flat' && change == null) {
    return <span className="text-xs font-medium text-gray-400">{label}</span>;
  }
  const Icon = tone === 'up' ? TrendingUp : tone === 'down' ? TrendingDown : Minus;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium',
        tone === 'up' && 'text-emerald-600',
        tone === 'down' && 'text-rose-600',
        tone === 'flat' && 'text-gray-400',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

function FilterSelect({
  value,
  onChange,
  children,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
  'aria-label'?: string;
}) {
  return (
    <select
      value={value}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 min-w-0 max-w-[11rem] rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-700 outline-none ring-blue-500 focus:ring-2 sm:max-w-none"
    >
      {children}
    </select>
  );
}

function SegmentedControl({
  'aria-label': ariaLabel,
  children,
}: {
  'aria-label': string;
  children: React.ReactNode;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex max-w-full flex-wrap rounded-lg border border-gray-200 bg-white p-1"
    >
      {children}
    </div>
  );
}

function SegmentButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-md px-2.5 py-1.5 text-sm font-medium transition-[color,background-color,transform] duration-150 ease-out active:scale-[0.97]',
        active ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
      )}
    >
      {children}
    </button>
  );
}

function ErPill({ rate }: { rate: number }) {
  const pct = rate * 100;
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
        pct >= 12 ? 'bg-emerald-50 text-emerald-700' : pct >= 6 ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600',
      )}
    >
      {formatEr(rate)}
    </span>
  );
}

function postsPhrase(n: number | undefined): string {
  if (n == null) return '';
  return n === 1 ? '1 post' : `${n} posts`;
}

function Heatmap({ cells, emptyLabel }: { cells: HeatCell[]; emptyLabel?: string }) {
  const posted = cells.filter((c) => c.value != null);
  if (posted.length === 0) {
    return (
      <div className="flex h-56 items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 text-center text-sm text-gray-500">
        {emptyLabel ?? 'No timing signal in this window yet.'}
      </div>
    );
  }
  const max = Math.max(0, ...posted.map((c) => c.value as number));
  const times = bestTimes(cells).filter((t) => (t.value ?? 0) > 0);
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-1 text-xs">
          <thead>
            <tr>
              <th className="w-8" />
              {HEAT_HOURS.map((h) => (
                <th key={h} className="pb-1 text-left font-medium text-gray-400">
                  {h % 3 === 0 ? hourLabel(h) : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {HEAT_DAYS.map((day) => (
              <tr key={day}>
                <th className="pr-1 text-left font-medium text-gray-500">{day}</th>
                {HEAT_HOURS.map((hour) => {
                  const cell = cells.find((c) => c.day === day && c.hour === hour);
                  const empty = cell?.value == null;
                  const zero = !empty && (cell?.value ?? 0) <= 0;
                  const t = max > 0 ? (cell?.value ?? 0) / max : 0;
                  const posts = postsPhrase(cell?.posts);
                  const title = empty
                    ? `${day} ${hourLabel(hour)} · no data`
                    : `${day} ${hourLabel(hour)} · ${cell!.value}${posts ? ` · ${posts}` : ''}`;
                  return (
                    <td key={hour}>
                      <div
                        title={title}
                        aria-label={title}
                        className={cn(
                          'h-6 w-full min-w-[12px] rounded-sm',
                          empty && 'ring-1 ring-inset ring-gray-200',
                        )}
                        style={{
                          backgroundColor: empty
                            ? 'transparent'
                            : zero || max <= 0
                              ? 'rgb(209 213 219)'
                              : `rgba(16, 185, 129, ${0.15 + t * 0.85})`,
                        }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {times.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
          Best times:
          {times.map((t) => (
            <span
              key={`${t.day}-${t.hour}`}
              title={t.posts != null ? postsPhrase(t.posts) : undefined}
              className="rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-800"
            >
              {t.day} {hourLabel(t.hour)}
              <span className="font-normal text-emerald-600"> · {Number(t.value).toFixed(1)}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const FORMAT_META = {
  video: { label: 'Video', icon: Video },
  image: { label: 'Image', icon: ImageIcon },
  carousel: { label: 'Carousel', icon: Layers },
  text: { label: 'Text', icon: Type },
} as const;

const PostingInsightsPage = (_props: { embedded?: boolean }) => {
  const [filters, setFilters] = useState<InsightFilters>(() => ({
    ...EMPTY_INSIGHT_FILTERS,
    ...loadOwnedRangePreference(),
  }));
  const [seriesOn, setSeriesOn] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      ENGAGEMENT_SERIES.map((s) => [s.key, ['likes', 'comments', 'views', 'impressions'].includes(s.key)]),
    ),
  );
  const [barMetric, setBarMetric] = useState<BarMetric>('likes');
  const [platformSort, setPlatformSort] = useState<{ key: SortKey | 'posts'; dir: 'asc' | 'desc' }>({
    key: 'posts',
    dir: 'desc',
  });
  const [grain, setGrain] = useState<TimeGrain>('week');
  const [engagementCumulative, setEngagementCumulative] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('views');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [drill, setDrill] = useState<PostDrill>(EMPTY_DRILL);
  const [postsPage, setPostsPage] = useState(1);
  const [postsLimit, setPostsLimit] = useState(10);
  const postsTableRef = useRef<HTMLDivElement>(null);
  const followersPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    saveOwnedRangePreference({ range: filters.range, customFrom: filters.customFrom });
  }, [filters.range, filters.customFrom]);

  const patch = (next: Partial<InsightFilters>) => {
    setPostsPage(1);
    setFilters((f) => ({ ...f, ...next }));
  };

  const scrollToPosts = () => {
    window.requestAnimationFrame(() => {
      postsTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const scrollToFollowers = () => {
    window.requestAnimationFrame(() => {
      followersPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  /** Jump to the posts table with optional platform / period / sort / highlight. */
  const revealPosts = (opts: {
    platform?: SocialPlatform | 'all';
    period?: string | null;
    sort?: SortKey;
    focusPostId?: string | null;
  }) => {
    if (opts.platform !== undefined) patch({ platform: opts.platform });
    setDrill({
      period: opts.period ?? null,
      focusPostId: opts.focusPostId ?? null,
    });
    if (opts.sort) {
      setSortKey(opts.sort);
      setSortDir(opts.sort === 'publishedAt' ? 'desc' : 'desc');
    }
    setPostsPage(1);
    scrollToPosts();
  };

  const clearDrill = () => {
    setDrill(EMPTY_DRILL);
    setPostsPage(1);
  };

  // Range, platform and source are applied SERVER-side; `handle` is not, because
  // the endpoint takes `said` rather than a handle string.
  const ownedWindow = useMemo(
    () => ownedWindowBounds(filters.range, filters.customFrom),
    [filters.range, filters.customFrom],
  );
  const owned = usePerformanceOwnedQuery({
    range_days: ownedWindow.range_days,
    from: ownedWindow.from,
    to: ownedWindow.to,
    platform: filters.platform === 'all' ? undefined : filters.platform,
    source: filters.source === 'all' ? undefined : filters.source,
  });
  const resync = useResyncOwnedMutation();
  const unsynced = !owned.isLoading && !owned.isError && isUnsynced(owned.data);

  /**
   * Always live rows. Do NOT call `filterPosts` / `filterDaily` / `filterFollowers`
   * — those helpers default to mock data and range-filter against a hardcoded
   * ANCHOR date, which silently drops real posts.
   */
  const posts = useMemo(() => {
    const rows = livePosts(owned.data);
    return filters.handle === 'all' ? rows : rows.filter((p) => p.handle === filters.handle);
  }, [owned.data, filters.handle]);

  const daily = useMemo(() => {
    const rows = liveDaily(owned.data);
    if (filters.handle === 'all') return rows;
    const plats = new Set(posts.map((p) => p.platform));
    return rows.filter((r) => plats.has(r.platform));
  }, [owned.data, filters.handle, posts]);

  const publishedDaily = useMemo(() => {
    const rows = livePublishedDaily(owned.data);
    if (filters.handle === 'all') return rows;
    const plats = new Set(posts.map((p) => p.platform));
    return rows.filter((r) => plats.has(r.platform));
  }, [owned.data, filters.handle, posts]);

  const followers = useMemo(() => {
    const rows = liveFollowers(owned.data);
    if (filters.handle === 'all') return rows;
    const plats = new Set(posts.map((p) => p.platform));
    return rows.filter((r) => plats.has(r.platform));
  }, [owned.data, filters.handle, posts]);

  const platforms = useMemo(() => uniquePlatforms(posts), [posts]);
  const handles = useMemo(
    () => [...new Set(livePosts(owned.data).map((p) => p.handle))].sort(),
    [owned.data],
  );
  const allPlatforms = useMemo(
    () => uniquePlatforms(livePosts(owned.data)),
    [owned.data],
  );

  const totals = useMemo(() => bagToMetrics(owned.data?.totals), [owned.data]);
  const prevTotals = useMemo(() => bagToMetrics(owned.data?.prior_totals), [owned.data]);
  const avgEr = owned.data?.kpis?.find((k) => k.id === 'kpi_engagement_rate')?.value ?? 0;
  const prevEr = owned.data?.kpis?.find((k) => k.id === 'kpi_engagement_rate')?.prior ?? null;
  const erChange =
    owned.data?.kpis?.find((k) => k.id === 'kpi_engagement_rate')?.change_pct ??
    changeVsPrior(avgEr, prevEr ?? 0);
  const erBasis = owned.data?.kpis?.find((k) => k.id === 'kpi_engagement_rate')?.basis;
  const reachKpi = owned.data?.kpis?.find((k) => k.id === 'kpi_reach');
  const postsKpi = owned.data?.kpis?.find((k) => k.id === 'kpi_posts');
  const postsCount = owned.data?.totals?.posts ?? posts.length;
  const priorPostsCount = owned.data?.prior_totals?.posts ?? 0;
  const followersNow = useMemo(() => {
    const lastByPlat = new Map<SocialPlatform, number>();
    for (const r of followers) lastByPlat.set(r.platform, r.followers);
    return [...lastByPlat.values()].reduce((s, n) => s + n, 0);
  }, [followers]);
  const followersPrev = useMemo(() => {
    const firstByPlat = new Map<SocialPlatform, number>();
    for (const r of followers) {
      if (!firstByPlat.has(r.platform)) firstByPlat.set(r.platform, r.followers);
    }
    return [...firstByPlat.values()].reduce((s, n) => s + n, 0);
  }, [followers]);
  const hasFollowerSeries = followers.length > 0 && followersNow > 0;
  const followerPlatforms = useMemo(() => {
    const set = new Set<SocialPlatform>();
    for (const r of followers) {
      if (r.followers > 0) set.add(r.platform);
    }
    return [...set];
  }, [followers]);

  const bestPost = useMemo(() => {
    if (posts.length === 0) return null;
    return [...posts].sort((a, b) => {
      const ae = a.engagement ?? a.metrics.likes + a.metrics.comments + a.metrics.shares;
      const be = b.engagement ?? b.metrics.likes + b.metrics.comments + b.metrics.shares;
      return be - ae || b.metrics.views - a.metrics.views;
    })[0];
  }, [posts]);
  const bestEngagement =
    bestPost == null
      ? 0
      : (bestPost.engagement ??
        bestPost.metrics.likes + bestPost.metrics.comments + bestPost.metrics.shares);

  const perPlatform = platforms.map((p) => ({
    platform: PLATFORM_LABEL[p],
    key: p,
    posts: posts.filter((x) => x.platform === p).length,
    value: posts.filter((x) => x.platform === p).reduce((s, x) => s + x.metrics[barMetric], 0),
    fill: PLATFORM_CHART_COLOR[p],
  }));
  // Ranked like the vendor's "Top 5" bars; an unsorted axis hides the leader.
  const postsPerPlatform = [...perPlatform].sort((a, b) => b.posts - a.posts);
  const metricPerPlatform = [...perPlatform].sort((a, b) => b.value - a.value);
  const metricTotal = perPlatform.reduce((s, r) => s + r.value, 0);

  const postsOverTime = useMemo(() => {
    const buckets = new Map<string, Record<string, string | number>>();
    const add = (date: string, platform: SocialPlatform, n: number) => {
      const b = bucketStart(date, grain);
      const row = buckets.get(b) ?? { bucket: b };
      row[platform] = Number(row[platform] ?? 0) + n;
      buckets.set(b, row);
    };
    if (publishedDaily.length > 0) {
      for (const r of publishedDaily) add(utcDayKey(r.date), r.platform, r.posts);
    } else {
      for (const p of posts) add(utcDayKey(p.publishedAt), p.platform, 1);
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, row]) => row);
  }, [publishedDaily, posts, grain]);

  // From posts by publish period, so it totals to the same number as the
  // per-platform bars beside it (the vendor's 352 likes, not daily-metrics' 357).
  const metricOverTime = useMemo(() => {
    const buckets = new Map<string, Record<string, string | number>>();
    for (const p of posts) {
      const b = bucketStart(utcDayKey(p.publishedAt), grain);
      const row = buckets.get(b) ?? { bucket: b };
      row[p.platform] = Number(row[p.platform] ?? 0) + p.metrics[barMetric];
      buckets.set(b, row);
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, row]) => row);
  }, [posts, barMetric, grain]);

  // Daily-metrics sums: what the vendor's engagement cards show (2.3K reach vs
  // the posts-based 1.5K on the KPI tile — two sources, as on Zernio).
  const dailyTotals = useMemo(() => {
    const t = bagToMetrics(undefined);
    for (const r of daily) {
      for (const k of BAR_METRICS) t[k] += r[k] ?? 0;
    }
    return t;
  }, [daily]);

  const engagementOverTime = useMemo(() => {
    const plats =
      filters.handle === 'all' ? undefined : new Set(posts.map((p) => p.platform));
    const rows = liveEngagementOverTime(owned.data, grain, plats);
    return engagementCumulative ? cumulateEngagement(rows) : rows;
  }, [owned.data, grain, engagementCumulative, filters.handle, posts]);
  const dailyUnsynced = isDailyUnsynced(owned.data);
  const hasEngagementSeries = !dailyUnsynced && engagementOverTime.length > 0;

  const followerLine = useMemo(() => {
    const byDate = new Map<string, Record<string, number | null>>();
    for (const r of followers) {
      const row = byDate.get(r.date) ?? {};
      // Null, not 0: the axis is logarithmic and cannot place a zero.
      row[r.platform] = r.followers > 0 ? r.followers : null;
      byDate.set(r.date, row);
    }
    return [...byDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, row]) => ({ date: utcDayKey(date), ...row }));
  }, [followers]);

  const platformRows = platforms
    .map((platform) => {
      const slice = posts.filter((p) => p.platform === platform);
      const m = sumMetrics(slice);
      return { platform, posts: slice.length, metrics: m, er: er(m) };
    })
    .sort((a, b) => {
      const pick = (r: typeof a) =>
        platformSort.key === 'posts'
          ? r.posts
          : platformSort.key === 'er'
            ? r.er
            : platformSort.key === 'publishedAt'
              ? 0
              : r.metrics[platformSort.key];
      return platformSort.dir === 'asc' ? pick(a) - pick(b) : pick(b) - pick(a);
    });

  const togglePlatformSort = (key: SortKey | 'posts') =>
    setPlatformSort((s) =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' },
    );

  const sortedPosts = useMemo(() => {
    let copy = [...posts];
    if (drill.period) {
      copy = copy.filter((p) => bucketStart(utcDayKey(p.publishedAt), grain) === drill.period);
    }
    copy.sort((a, b) => {
      if (drill.focusPostId) {
        if (a.id === drill.focusPostId) return -1;
        if (b.id === drill.focusPostId) return 1;
      }
      const av =
        sortKey === 'publishedAt'
          ? Date.parse(a.publishedAt)
          : sortKey === 'er'
            ? er(a.metrics)
            : a.metrics[sortKey];
      const bv =
        sortKey === 'publishedAt'
          ? Date.parse(b.publishedAt)
          : sortKey === 'er'
            ? er(b.metrics)
            : b.metrics[sortKey];
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return copy;
  }, [posts, sortKey, sortDir, drill, grain]);

  const drillActive = Boolean(drill.period || drill.focusPostId);

  const pagedPosts = useMemo(() => {
    const start = (postsPage - 1) * postsLimit;
    return sortedPosts.slice(start, start + postsLimit);
  }, [sortedPosts, postsPage, postsLimit]);

  useEffect(() => {
    const last = Math.max(1, Math.ceil(sortedPosts.length / postsLimit) || 1);
    if (postsPage > last) setPostsPage(last);
  }, [sortedPosts.length, postsLimit, postsPage]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'publishedAt' ? 'desc' : 'desc');
    }
    setPostsPage(1);
  };

  const accumulation = useMemo(() => liveContentDecay(owned.data), [owned.data]);
  const hasAccumulation = accumulation.length > 0 && accumulation.some((p) => p.pct > 0);
  const accumulationIsCumulative = accumulation.every(
    (p, i) => i === 0 || p.pct + 0.5 >= accumulation[i - 1].pct,
  );

  const accumulationInsight = useMemo(() => {
    if (!hasAccumulation || !accumulationIsCumulative) return null;
    const half = accumulation.find((p) => p.pct >= 50);
    const eighty = accumulation.find((p) => p.pct >= 80);
    const parts: string[] = [];
    if (half) parts.push(`Half of engagement by ${half.bucket}`);
    if (eighty) parts.push(`80% within ${eighty.bucket}`);
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [accumulation, hasAccumulation, accumulationIsCumulative]);

  // A cumulative curve starts at 0% on publish; the vendor's first bucket is
  // already 0-6h, so without this anchor the area opens mid-air.
  const accumulationCurve = useMemo(
    () =>
      accumulationIsCumulative
        ? [{ order: -1, bucket: 'Publish', pct: 0, posts: undefined }, ...accumulation]
        : accumulation,
    [accumulation, accumulationIsCumulative],
  );

  const cadence = useMemo(() => livePostingFrequency(owned.data), [owned.data]);
  const cadenceBest = useMemo(() => bestCadence(cadence), [cadence]);
  const formats = useMemo(() => formatBreakdown(posts, er, sumMetrics), [posts]);
  const heat = useMemo(() => liveHeat(owned.data, posts), [owned.data, posts]);

  const requestResync = () => {
    const days = Math.min(OWNED_MAX_RANGE_DAYS, Math.max(1, filters.range));
    resync.mutate(
      {
        source: filters.source === 'all' ? 'all' : filters.source,
        days,
        full: days >= OWNED_MAX_RANGE_DAYS,
      },
      {
        onSuccess: () => {
          toast.success(days >= OWNED_MAX_RANGE_DAYS ? 'Full-year sync queued' : 'Sync queued', {
            description:
              'Pulling connected analytics in the background. This page refreshes when data arrives.',
          });
          let tries = 0;
          const id = window.setInterval(() => {
            tries += 1;
            void owned.refetch().then((r) => {
              const points = r.data?.store?.follower_points ?? 0;
              if (points > 0 || tries >= 24) window.clearInterval(id);
            });
          }, 15_000);
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not enqueue sync')),
      },
    );
  };

  // Sums of the plotted daily-metrics, so each card is the total of its line.
  // Vendor Eng. rate here is (likes+comments+shares+saves)/impressions — 13.48%
  // on Zernio, a different number from the KPI tile's by design.
  const dailyEr = dailyTotals.impressions
    ? (dailyTotals.likes + dailyTotals.comments + dailyTotals.shares + dailyTotals.saves) /
      dailyTotals.impressions
    : 0;
  const metricCards: { label: string; series?: string; value: number; percent?: boolean }[] = [
    ...ENGAGEMENT_SERIES.map((s) => ({ label: s.label, series: s.key, value: dailyTotals[s.key] })),
    { label: 'Eng. rate', value: dailyEr, percent: true },
  ];

  const kpis = [
    {
      label: 'Engagement rate',
      value: formatEr(avgEr),
      change: erChange,
      hint: erBasis ? `on ${erBasis}` : undefined,
    },
    {
      label: 'Total reach',
      value: formatCompact(reachKpi?.value ?? totals.reach),
      change: reachKpi?.change_pct ?? changeVsPrior(totals.reach, prevTotals.reach),
    },
    {
      label: 'Followers',
      value: hasFollowerSeries ? formatCompact(followersNow) : '—',
      change: hasFollowerSeries ? changeVsPrior(followersNow, followersPrev) : null,
      // Absolute gain reads better than % on small counts (Zernio: "+223 in last 30d").
      hint: hasFollowerSeries
        ? `${followersNow - followersPrev >= 0 ? '+' : ''}${followersNow - followersPrev} in window`
        : 'no series yet',
    },
    {
      label: 'Posts',
      value: String(postsKpi?.value ?? postsCount),
      change: postsKpi?.change_pct ?? changeVsPrior(postsCount, priorPostsCount),
    },
  ];

  const syncButton = (
    <Button
      type="button"
      size="sm"
      disabled={resync.isPending}
      onClick={requestResync}
    >
      {resync.isPending ? (
        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
      ) : (
        <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
      )}
      Sync analytics
    </Button>
  );

  const showCustomRange =
    filters.customFrom != null ||
    !OWNED_RANGE_PRESETS.includes(filters.range as (typeof OWNED_RANGE_PRESETS)[number]);
  const windowStart = ownedWindow.from;

  const body = (
    <>
      {owned.isError ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-950">
          <p className="font-medium">Could not load owned performance</p>
          <p className="mt-1 text-red-900/90">
            {getApiErrorMessage(owned.error, 'Check marketing.view permission and try again.')}
          </p>
        </div>
      ) : null}

      {owned.isLoading ? (
        <div className="rounded-xl border border-gray-100 bg-white px-6 py-16 text-center text-sm text-gray-500">
          Loading connected analytics…
        </div>
      ) : unsynced ? (
        <div className="rounded-xl border border-gray-100 bg-white px-6 py-14 text-center shadow-sm">
          <p className="text-base font-semibold text-gray-900">No connected analytics yet</p>
          <p className="mx-auto mt-2 max-w-lg text-sm text-gray-500">
            This tab reads a snapshot store — it does not call Zernio on every page load (that would
            time out and burn quota). Sync once to pull impressions, reach, and followers for your
            connected accounts; hourly beats keep it fresh after that.
          </p>
          <div className="mt-5 flex justify-center">{syncButton}</div>
          <p className="mt-3 text-xs text-gray-400">Requires marketing.manage</p>
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-950">
            <div className="min-w-0">
              <p className="font-medium">
                Live — connected basis
                {owned.data?.as_of ? ` · synced ${new Date(owned.data.as_of).toLocaleString()}` : ''}
              </p>
              <p className="mt-1 text-emerald-900/90">
                {owned.data?.store.posts ?? 0} posts across {owned.data?.store.accounts ?? 0}{' '}
                connected account(s). Do not compare with Tracked (public surface).
                {hasAccountStep(owned.data)
                  ? ' Follower line may step when an account was added — that is not growth.'
                  : ''}
              </p>
            </div>
            {syncButton}
          </div>
      <div className="mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <SegmentedControl aria-label="Date range">
            {OWNED_RANGE_PRESETS.map((d) => (
              <SegmentButton
                key={d}
                active={!showCustomRange && filters.range === d}
                onClick={() => {
                  clearDrill();
                  patch({ range: d, customFrom: null });
                }}
              >
                {d === 365 ? '12 mo' : `${d}d`}
              </SegmentButton>
            ))}
            <SegmentButton
              active={showCustomRange}
              onClick={() => {
                clearDrill();
                const from = filters.customFrom ?? ownedInclusiveStart(filters.range);
                patch({ customFrom: from, range: ownedDaysFromStart(from) });
              }}
            >
              Custom
            </SegmentButton>
          </SegmentedControl>
          <SegmentedControl aria-label="Group by">
            {(['day', 'week', 'month'] as const).map((g) => (
              <SegmentButton
                key={g}
                active={grain === g}
                onClick={() => {
                  clearDrill();
                  setGrain(g);
                }}
              >
                {GRAIN_LABEL[g]}
              </SegmentButton>
            ))}
          </SegmentedControl>
          {showCustomRange ? (
            <label className="inline-flex items-center gap-2 text-sm text-gray-600">
              <span className="whitespace-nowrap">From</span>
              <input
                type="date"
                className="h-9 rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-700 outline-none ring-blue-500 focus:ring-2"
                max={new Date().toISOString().slice(0, 10)}
                min={ownedStartDateForDays(OWNED_MAX_RANGE_DAYS)}
                value={windowStart}
                onChange={(e) => {
                  const from = e.target.value;
                  if (!from) return;
                  clearDrill();
                  patch({ customFrom: from, range: ownedDaysFromStart(from) });
                }}
              />
              <span className="tabular-nums text-xs text-gray-400">
                → today · {servedWindowLabel(owned.data?.range_days, filters.range)}
              </span>
            </label>
          ) : (
            <span className="text-xs tabular-nums text-gray-400">
              {windowStart} → today · {servedWindowLabel(owned.data?.range_days, filters.range)}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect
            aria-label="Platform"
            value={filters.platform}
            onChange={(v) => {
              clearDrill();
              patch({ platform: v as InsightFilters['platform'] });
            }}
          >
            <option value="all">All platforms</option>
            {allPlatforms.map((p) => (
              <option key={p} value={p}>
                {PLATFORM_LABEL[p]}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            aria-label="Profile"
            value={filters.handle}
            onChange={(v) => {
              clearDrill();
              patch({ handle: v });
            }}
          >
            <option value="all">All profiles</option>
            {handles.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </FilterSelect>
          <SegmentedControl aria-label="Post source">
            {(
              [
                { id: 'all', label: 'All' },
                { id: 'late', label: 'Studio' },
                { id: 'external', label: 'On-platform' },
              ] as const
            ).map((opt) => (
              <SegmentButton
                key={opt.id}
                active={filters.source === opt.id}
                onClick={() => {
                  clearDrill();
                  patch({ source: opt.id });
                }}
              >
                {opt.label}
              </SegmentButton>
            ))}
          </SegmentedControl>
          <p className="ml-auto max-w-md text-right text-xs text-gray-400">
            Connected accounts only.{' '}
            <Link
              to="/admin/marketing/radar/performance?tab=tracked"
              className="font-medium text-gray-500 underline-offset-2 hover:text-blue-700 hover:underline"
            >
              Tracked
            </Link>{' '}
            for listen-only handles.
          </p>
        </div>
      </div>

      {posts.length === 0 ? (
        <EmptyState label="No owned posts in this window — try a wider range or Sync analytics again." />
      ) : (
        <>
          <div className="mb-4 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {kpis.map((k) => {
                const drillKey = KPI_SORT[k.label];
                return (
                  <button
                    key={k.label}
                    type="button"
                    onClick={() => {
                      if (drillKey === 'followers') {
                        scrollToFollowers();
                        return;
                      }
                      if (drillKey) revealPosts({ sort: drillKey });
                      else scrollToPosts();
                    }}
                    className="rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-left transition hover:border-gray-300 hover:bg-gray-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-200"
                    title={
                      drillKey === 'followers'
                        ? 'Jump to follower evolution'
                        : 'Show underlying posts sorted by this metric'
                    }
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        {k.label}
                      </p>
                      <Change change={k.change} />
                    </div>
                    <p className="mt-1.5 text-2xl font-semibold tabular-nums tracking-tight text-gray-900">
                      {k.value}
                    </p>
                    {k.hint ? (
                      <p className="mt-0.5 text-[11px] text-gray-400">{k.hint}</p>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {bestPost ? (
              <button
                type="button"
                onClick={() =>
                  revealPosts({
                    platform: bestPost.platform,
                    focusPostId: bestPost.id,
                    sort: 'views',
                  })
                }
                className="w-full rounded-xl border border-gray-200 bg-white p-4 text-left transition hover:border-gray-300 hover:bg-gray-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-200"
                title="Jump to this post in the table"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                      Best post
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">Highest engagement in this window · click to view in table</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <PlatformLogoBadge platform={bestPost.platform} size="sm" />
                    <span className="font-medium text-gray-700">{PLATFORM_LABEL[bestPost.platform]}</span>
                    <span className="text-gray-300">·</span>
                    <span className="truncate">{bestPost.handle}</span>
                    <span className="text-gray-300">·</span>
                    <span>{bestPost.mediaLabel}</span>
                  </div>
                </div>
                <div className="mt-3 flex gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-sm font-medium leading-snug text-gray-900">
                      {bestPost.caption || 'Untitled post'}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                      <span className="font-semibold tabular-nums text-gray-800">
                        {formatCompact(bestEngagement)} eng
                      </span>
                      <span className="tabular-nums">{formatEr(er(bestPost.metrics))} ER</span>
                      {bestPost.metrics.views > 0 ? (
                        <span className="tabular-nums">{formatCompact(bestPost.metrics.views)} views</span>
                      ) : null}
                      {bestPost.metrics.reach > 0 ? (
                        <span className="tabular-nums">{formatCompact(bestPost.metrics.reach)} reach</span>
                      ) : null}
                      {bestPost.publishedAt && Date.parse(bestPost.publishedAt) > 0 ? (
                        <span>
                          {new Date(bestPost.publishedAt).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {bestPost.url ? (
                    <a
                      href={bestPost.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex h-9 shrink-0 items-center gap-1.5 self-center rounded-md border border-gray-200 px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      Open
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : null}
                </div>
              </button>
            ) : null}
          </div>

          <div className="mb-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Posts per platform" action={<span className="text-xs tabular-nums text-gray-400"><span className="text-base font-semibold text-gray-900">{postsCount}</span> posts total</span>}>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={postsPerPlatform} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="platform" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                    <Tooltip />
                    <Bar
                      dataKey="posts"
                      name="Posts"
                      radius={[4, 4, 0, 0]}
                      cursor="pointer"
                      onClick={(data) => {
                        const row = chartRow<{ key?: SocialPlatform }>(data);
                        if (row?.key) revealPosts({ platform: row.key, sort: 'publishedAt' });
                      }}
                    >
                      {postsPerPlatform.map((r) => (
                        <Cell
                          key={r.key}
                          fill={r.fill}
                          opacity={filters.platform === 'all' || filters.platform === r.key ? 1 : 0.35}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-gray-400">Click a bar to filter posts by platform</p>
            </Panel>
            <Panel title="Posts over time" action={<span className="text-xs tabular-nums text-gray-400"><span className="text-base font-semibold text-gray-900">{postsCount}</span> posts total</span>}>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={postsOverTime} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} tickFormatter={(b: string) => bucketTick(b, grain)} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                    <Tooltip labelFormatter={(b: string) => periodLabel(b, grain)} />
                    {platforms.map((p) => (
                      <Bar
                        key={p}
                        dataKey={p}
                        stackId="p"
                        name={PLATFORM_LABEL[p]}
                        fill={PLATFORM_CHART_COLOR[p]}
                        cursor="pointer"
                        onClick={(data) => {
                          const row = chartRow<{ bucket?: string }>(data);
                          if (row?.bucket) revealPosts({ period: row.bucket, platform: p, sort: 'publishedAt' });
                        }}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-gray-400">Click a stack segment for that {grain} + platform</p>
            </Panel>
            <Panel
              title={`${METRIC_LABEL[barMetric]} per platform`}
              action={
                <div className="flex items-center gap-2">
                  <span className="text-xs tabular-nums text-gray-400"><span className="text-base font-semibold text-gray-900">{formatCompact(metricTotal)}</span> {METRIC_LABEL[barMetric].toLowerCase()} total</span>
                  <FilterSelect aria-label="Metric" value={barMetric} onChange={(v) => setBarMetric(v as BarMetric)}>
                    {BAR_METRICS.map((m) => (
                      <option key={m} value={m}>
                        {METRIC_LABEL[m]}
                      </option>
                    ))}
                  </FilterSelect>
                </div>
              }
            >
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={metricPerPlatform} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="platform" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={36} />
                    <Tooltip />
                    <Bar
                      dataKey="value"
                      name={METRIC_LABEL[barMetric]}
                      radius={[4, 4, 0, 0]}
                      cursor="pointer"
                      onClick={(data) => {
                        const row = chartRow<{ key?: SocialPlatform }>(data);
                        if (row?.key) revealPosts({ platform: row.key, sort: barMetric });
                      }}
                    >
                      {metricPerPlatform.map((r) => (
                        <Cell
                          key={r.key}
                          fill={r.fill}
                          opacity={filters.platform === 'all' || filters.platform === r.key ? 1 : 0.35}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-gray-400">Click a bar to filter posts by platform</p>
            </Panel>
            <Panel title={`${METRIC_LABEL[barMetric]} over time`} action={<span className="text-xs tabular-nums text-gray-400"><span className="text-base font-semibold text-gray-900">{formatCompact(metricTotal)}</span> {METRIC_LABEL[barMetric].toLowerCase()} total</span>}>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={metricOverTime} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} tickFormatter={(b: string) => bucketTick(b, grain)} />
                    <YAxis tick={{ fontSize: 11 }} width={36} />
                    <Tooltip labelFormatter={(b: string) => periodLabel(b, grain)} />
                    {platforms.map((p) => (
                      <Bar
                        key={p}
                        dataKey={p}
                        stackId="l"
                        name={PLATFORM_LABEL[p]}
                        fill={PLATFORM_CHART_COLOR[p]}
                        cursor="pointer"
                        onClick={(data) => {
                          const row = chartRow<{ bucket?: string }>(data);
                          if (row?.bucket) revealPosts({ period: row.bucket, platform: p, sort: barMetric });
                        }}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-gray-400">Click a stack segment for that {grain} + platform</p>
            </Panel>
          </div>

          <div className="mb-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
            <Panel
              title="Engagement over time"
              action={
                <div className="flex flex-wrap items-center gap-3">
                  <SegmentedControl aria-label="Engagement totals">
                    <SegmentButton
                      active={!engagementCumulative}
                      onClick={() => setEngagementCumulative(false)}
                    >
                      Per period
                    </SegmentButton>
                    <SegmentButton
                      active={engagementCumulative}
                      onClick={() => setEngagementCumulative(true)}
                    >
                      Cumulative
                    </SegmentButton>
                  </SegmentedControl>
                </div>
              }
            >
              {hasEngagementSeries ? (
                <>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={engagementOverTime}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                    style={{ cursor: 'pointer' }}
                    onClick={(state) => {
                      const payload = (
                        state as {
                          activePayload?: { payload?: { date?: string } }[];
                        } | null
                      )?.activePayload?.[0]?.payload;
                      if (payload?.date) revealPosts({ period: payload.date, sort: 'views' });
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(d: string) => bucketTick(d, grain)}
                    />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={36} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={40} />
                    <Tooltip
                      labelFormatter={(d: string) => periodLabel(d, grain)}
                    />
                    <Legend />
                    {ENGAGEMENT_SERIES.filter((s) => seriesOn[s.key]).map((s) => (
                      <Line
                        key={s.key}
                        yAxisId={RIGHT_AXIS.has(s.key) ? 'right' : 'left'}
                        type="monotone"
                        dataKey={s.key}
                        name={s.label}
                        stroke={s.color}
                        strokeWidth={2}
                        dot={false}
                        connectNulls={false}
                        activeDot={{ r: 4 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-gray-400">
                Vendor daily-metrics, bucketed by the post’s publish day — not inbound activity date.
                Week matches Zernio’s grain.
                {engagementCumulative ? ' Cumulative = running total across the window.' : ''} Click a {grain} to
                list posts from it.
              </p>
                </>
              ) : (
                <div className="flex h-72 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-6 text-center">
                  <p className="text-sm text-gray-500">
                    {dailyUnsynced
                      ? 'No vendor daily metrics yet. This chart is not posts published that day — missing is not a flat line.'
                      : 'No engagement in this window.'}
                  </p>
                  {dailyUnsynced && (owned.data?.store.posts ?? 0) > 0 ? (
                    <p className="text-xs text-amber-700">
                      Posts are in the store ({owned.data?.store.posts}), but daily-metrics have not
                      landed. The 6-hourly beat fills this; a full-year sync queues that pull now.
                    </p>
                  ) : null}
                  {dailyUnsynced ? <div>{syncButton}</div> : null}
                </div>
              )}
            </Panel>
            <div className="grid grid-cols-3 gap-2">
              {metricCards.map((c) => {
                const sort = METRIC_CARD_SORT[c.label];
                const on = c.series ? (seriesOn[c.series] ?? false) : false;
                return (
                  <div
                    key={c.label}
                    className={cn(
                      'rounded-xl border border-gray-100 bg-white px-3 py-2.5 shadow-sm',
                      sort && sortKey === sort && 'border-teal-300 ring-1 ring-teal-100',
                    )}
                  >
                    {/* Checkbox plots the line (the vendor's cards are its legend);
                        the number sorts the posts table. Two controls, not nested. */}
                    <label className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      {c.series ? (
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => setSeriesOn((m) => ({ ...m, [c.series!]: !m[c.series!] }))}
                        />
                      ) : null}
                      {c.label}
                    </label>
                    <button
                      type="button"
                      disabled={!sort}
                      onClick={() => sort && revealPosts({ sort })}
                      title={sort ? `Sort posts by ${c.label}` : undefined}
                      className="mt-0.5 rounded text-left text-lg font-semibold tabular-nums text-gray-900 enabled:hover:text-teal-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-200"
                    >
                      {c.percent ? formatEr(c.value) : formatCompact(c.value)}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-4 grid gap-4 lg:grid-cols-2">
            <Panel
              title="Best time to post"
              action={
                heat.source === 'vendor' ? (
                  <span className="text-xs text-gray-400">Vendor window · UTC</span>
                ) : heat.source === 'posts' ? (
                  // Estimated from this window's posts — not the vendor heatmap.
                  <span className="text-xs text-amber-700">Estimated from posts</span>
                ) : null
              }
            >
              <Heatmap
                cells={heat.cells}
                emptyLabel="No best-time data yet. Sync analytics to pull the vendor heatmap, or widen the window once posts exist."
              />
              {heat.source === 'posts' ? (
                <p className="mt-2 text-xs text-gray-500">
                  Vendor best-time has not synced into the store yet. This grid weights when your
                  posts in this window were published by engagement — not the same as Zernio’s
                  recommendation.
                </p>
              ) : null}
            </Panel>
            <div ref={followersPanelRef}>
            <Panel
              title="Follower evolution"
              action={
                <span className="text-xs text-gray-400">
                  {hasFollowerSeries ? `${formatCompact(followersNow)} total` : '—'}
                </span>
              }
            >
              {hasFollowerSeries ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={followerLine} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={utcDayTick} />
                      {/* Log, as on the vendor's chart: a 100-follower page and a
                          5-follower one both stay readable on one axis. */}
                      <YAxis
                        tick={{ fontSize: 11 }}
                        width={48}
                        scale="log"
                        domain={[1, 'auto']}
                        allowDataOverflow
                      />
                      <Tooltip labelFormatter={(d: string) => utcDayKey(d) || d} />
                      <Legend />
                      {followerPlatforms.map((p) => (
                        <Line
                          key={p}
                          type="monotone"
                          dataKey={p}
                          name={PLATFORM_LABEL[p]}
                          stroke={PLATFORM_CHART_COLOR[p]}
                          strokeWidth={2}
                          // A platform that only has daily snapshots starts with one
                          // point, which a dot-less line would not draw at all.
                          dot={followers.filter((r) => r.platform === p).length <= 3 ? { r: 3 } : false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-6 text-center">
                  <p className="text-sm text-gray-500">
                    No follower series yet. Posts and follower history are different vendor pulls —
                    a post sync does not fill this chart. Missing is not the same as zero.
                  </p>
                  {(owned.data?.store.posts ?? 0) > 0 &&
                  (owned.data?.store.follower_points ?? 0) === 0 ? (
                    <p className="text-xs text-amber-700">
                      Posts are in the store ({owned.data?.store.posts}), but follower points have
                      not landed. The connected follower pull is still a snapshot (current count
                      only) — Sync queues that same pull and cannot invent a curve.
                    </p>
                  ) : null}
                  <div>{syncButton}</div>
                </div>
              )}
            </Panel>
            </div>
          </div>

          <div className="mb-4 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-900">Platform breakdown</h3>
              <p className="mt-0.5 text-xs text-gray-400">
                One row per platform in this window · click a row to filter posts
              </p>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Platform</th>
                  {(
                    [
                      ['posts', 'Posts'],
                      ['likes', 'Likes'],
                      ['comments', 'Comments'],
                      ['shares', 'Shares'],
                      ['saves', 'Saves'],
                      ['clicks', 'Clicks'],
                      ['views', 'Views'],
                      ['impressions', 'Impr.'],
                      ['reach', 'Reach'],
                      ['er', 'ER'],
                    ] as [SortKey | 'posts', string][]
                  ).map(([key, label]) => (
                    <th key={key} className="px-3 py-2 font-medium">
                      <button type="button" className="hover:text-gray-800" onClick={() => togglePlatformSort(key)}>
                        {label}
                        {platformSort.key === key ? (platformSort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {platformRows.map((r) => (
                  <tr
                    key={r.platform}
                    className={cn(
                      'cursor-pointer transition hover:bg-gray-50',
                      filters.platform === r.platform && 'bg-teal-50/60',
                    )}
                    onClick={() => revealPosts({ platform: r.platform, sort: 'views' })}
                    title={`Show ${PLATFORM_LABEL[r.platform]} posts`}
                  >
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-2 font-medium text-gray-900">
                        <PlatformLogoBadge platform={r.platform} size="sm" />
                        {PLATFORM_LABEL[r.platform]}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 tabular-nums">{r.posts}</td>
                    <td className="px-3 py-2.5 tabular-nums">{formatCompact(r.metrics.likes)}</td>
                    <td className="px-3 py-2.5 tabular-nums">{r.metrics.comments}</td>
                    <td className="px-3 py-2.5 tabular-nums">{r.metrics.shares}</td>
                    <td className="px-3 py-2.5 tabular-nums">{r.metrics.saves}</td>
                    <td className="px-3 py-2.5 tabular-nums">{r.metrics.clicks}</td>
                    <td className="px-3 py-2.5 tabular-nums">{formatCompact(r.metrics.views)}</td>
                    <td className="px-3 py-2.5 tabular-nums">{formatCompact(r.metrics.impressions)}</td>
                    <td className="px-3 py-2.5 tabular-nums">{formatCompact(r.metrics.reach)}</td>
                    <td className="px-3 py-2.5">
                      <ErPill rate={r.er} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          <div
            ref={postsTableRef}
            id="owned-posts-table"
            className="mb-4 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm scroll-mt-20"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900">Top performing posts</h3>
                <p className="mt-0.5 text-xs text-gray-400">
                  {sortedPosts.length} of {posts.length} in view
                  {filters.platform !== 'all' ? ` · ${PLATFORM_LABEL[filters.platform]}` : ''}
                  {drill.period ? ` · ${periodLabel(drill.period, grain)}` : ''}
                  {' · from synced top posts, not a full archive'}
                </p>
              </div>
              {drillActive ? (
                <Button type="button" variant="outline" size="sm" onClick={clearDrill}>
                  <X className="mr-1.5 h-3.5 w-3.5" />
                  Clear {grain} focus
                </Button>
              ) : null}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    {(
                      [
                        ['publishedAt', 'Post'],
                        ['likes', 'Likes'],
                        ['comments', 'Comments'],
                        ['shares', 'Shares'],
                        ['saves', 'Saves'],
                        ['clicks', 'Clicks'],
                        ['views', 'Views'],
                        ['follows', 'Follows'],
                        ['impressions', 'Impr.'],
                        ['reach', 'Reach'],
                        ['er', 'ER'],
                      ] as [SortKey, string][]
                    ).map(([key, label]) => (
                      <th
                        key={key}
                        className={cn(
                          'px-3 py-2 font-medium first:px-4',
                          key === 'publishedAt' && 'w-[1%] min-w-[12rem] max-w-[20rem]',
                        )}
                      >
                        <button type="button" className="hover:text-gray-800" onClick={() => toggleSort(key)}>
                          {label}
                          {sortKey === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedPosts.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-10 text-center text-sm text-gray-400">
                        No posts match this drill-down. Clear the day/week focus or widen filters.
                      </td>
                    </tr>
                  ) : (
                    pagedPosts.map((p) => {
                    const caption = p.caption?.trim() || 'Untitled post';
                    const focused = drill.focusPostId === p.id;
                    const postCell = (
                      <>
                        <PlatformLogoBadge platform={p.platform} size="sm" />
                        <div className="min-w-0 max-w-[14rem] sm:max-w-[18rem]">
                          <p
                            className={cn(
                              'truncate font-medium',
                              p.url ? 'text-teal-800 group-hover:underline' : 'text-gray-900',
                            )}
                            title={caption}
                          >
                            {caption}
                          </p>
                          <p className="truncate text-[11px] text-gray-400">
                            {utcDayKey(p.publishedAt)} · {p.handle}
                            {p.mediaLabel ? ` · ${p.mediaLabel}` : ''}
                            {!p.url ? ' · no permalink in sync' : ''}
                          </p>
                        </div>
                      </>
                    );
                    return (
                      <tr
                        key={p.id}
                        className={cn(
                          'hover:bg-gray-50',
                          focused && 'bg-teal-50 ring-1 ring-inset ring-teal-200',
                        )}
                      >
                        <td className="w-[1%] whitespace-nowrap px-4 py-2.5">
                          {p.url ? (
                            <a
                              href={p.url}
                              target="_blank"
                              rel="noreferrer"
                              className="group inline-flex max-w-full items-center gap-2 rounded-md outline-none ring-blue-500 hover:bg-gray-50 focus-visible:ring-2"
                              title="Open original post"
                            >
                              {postCell}
                              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-teal-600/70 transition-colors group-hover:text-teal-700" />
                            </a>
                          ) : (
                            <div
                              className="inline-flex max-w-full items-center gap-2"
                              title="Sync did not include a permalink for this post (common for some Instagram/Facebook ids). Re-sync analytics or open the profile on-platform."
                            >
                              {postCell}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums">{formatCompact(p.metrics.likes)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{formatCompact(p.metrics.comments)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{formatCompact(p.metrics.shares)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{formatCompact(p.metrics.saves)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{formatCompact(p.metrics.clicks)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{formatCompact(p.metrics.views)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{formatCompact(p.metrics.follows)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{formatCompact(p.metrics.impressions)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{formatCompact(p.metrics.reach)}</td>
                        <td className="px-3 py-2.5">
                          <ErPill rate={er(p.metrics)} />
                        </td>
                      </tr>
                    );
                  })
                  )}
                </tbody>
              </table>
            </div>
            {sortedPosts.length > 0 ? (
              <div className="border-t border-gray-100 px-4 py-3">
                <Pagination
                  total={sortedPosts.length}
                  page={postsPage}
                  limit={postsLimit}
                  itemsPerPage={postsLimit}
                  setPage={setPostsPage}
                  setItemsPerPage={(n) => {
                    setPostsLimit(n);
                    setPostsPage(1);
                  }}
                  showSelect
                  selectOptions={[10, 20, 50]}
                  compact
                />
              </div>
            ) : null}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Posting frequency vs engagement">
              {cadence.length > 0 ? (
                <>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis
                          type="number"
                          dataKey="posts_per_week"
                          name="Posts / week"
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          type="number"
                          dataKey="er"
                          name="ER"
                          unit="%"
                          tick={{ fontSize: 11 }}
                          width={40}
                        />
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                        <Legend />
                        {/* The vendor sends one row per cadence bucket (1/wk, 2/wk…),
                            several per platform — one series per platform, not per row. */}
                        {[...new Set(cadence.map((row) => row.platform))].map((platform) => (
                          <Scatter
                            key={platform}
                            name={PLATFORM_LABEL[platform]}
                            data={cadence.filter((row) => row.platform === platform)}
                            fill={PLATFORM_CHART_COLOR[platform]}
                            line={{ stroke: PLATFORM_CHART_COLOR[platform], strokeWidth: 2 }}
                            lineType="joint"
                          />
                        ))}
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                  {cadenceBest.length > 0 ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-gray-500">Best cadence:</span>
                      {cadenceBest.map((row) => (
                        <span
                          key={row.platform}
                          className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700"
                        >
                          <span className="font-medium">{PLATFORM_LABEL[row.platform]}</span>{' '}
                          {row.posts_per_week}/wk · {row.er.toFixed(1)}%
                          {row.weeks != null ? (
                            <span className="text-gray-400">
                              {' '}
                              ({row.weeks} wk{row.weeks === 1 ? '' : 's'})
                            </span>
                          ) : null}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <p className="mt-2 text-xs text-gray-500">
                    Engagement rate in weeks a platform posted that often (last 90 days), joined
                    per platform. X is posts per week, not a date; a lone dot is a platform with
                    one cadence. Few weeks behind a point = noisy.
                  </p>
                </>
              ) : (
                <div className="flex h-56 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-6 text-center">
                  <p className="text-sm text-gray-500">
                    No cadence scatter yet. This is posts/week vs engagement, not a timeline —
                    missing is not a flat line.
                  </p>
                </div>
              )}
            </Panel>
            <Panel title="Engagement accumulation">
              <p className="mb-2 text-xs text-gray-500">How engagement accumulates after publishing</p>
              {hasAccumulation ? (
                <>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={accumulationCurve} margin={{ top: 8, right: 28, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} width={36} unit="%" domain={[0, 100]} />
                        <Tooltip />
                        {accumulationIsCumulative
                          ? [50, 80].map((y) => (
                              <ReferenceLine
                                key={y}
                                y={y}
                                stroke="#9ca3af"
                                strokeDasharray="4 4"
                                label={{ value: `${y}%`, position: 'right', fontSize: 10, fill: '#6b7280' }}
                              />
                            ))
                          : null}
                        <Area
                          type="monotone"
                          dot={{ r: 3 }}
                          dataKey="pct"
                          name="Share"
                          stroke="#2563eb"
                          fill="#2563eb"
                          fillOpacity={0.18}
                          connectNulls={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  {accumulationInsight ? (
                    <p className="mt-2 text-xs text-gray-500">{accumulationInsight}</p>
                  ) : null}
                </>
              ) : (
                <div className="flex h-56 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-6 text-center">
                  <p className="text-sm text-gray-500">
                    No accumulation buckets yet. Sync analytics so the vendor can fill content decay —
                    missing is not the same as a flat line at zero.
                  </p>
                </div>
              )}
            </Panel>
          </div>

          <Panel
            title="Content format breakdown"
            className="mt-4"
            action={
              formats.rows.length > 0 ? (
                <span className="text-xs text-gray-500">avg {formats.avg.toFixed(1)}%</span>
              ) : null
            }
          >
            {formats.rows.length > 0 ? (
              <>
                <div className="space-y-2">
                  {formats.rows.map((row, i) => {
                    const Icon = FORMAT_META[row.format].icon;
                    const best = i === 0 && formats.rows.length > 1;
                    const top = formats.rows[0].er || 1;
                    return (
                      <div
                        key={row.format}
                        className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
                          best ? 'border-red-200 bg-red-50/60' : 'border-gray-100'
                        }`}
                      >
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                            best ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          <Icon className="h-4 w-4" aria-hidden />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            {FORMAT_META[row.format].label}
                            {best ? (
                              <span className="inline-flex items-center gap-1 rounded border border-red-200 px-1.5 text-[10px] font-semibold uppercase text-red-600">
                                <Trophy className="h-3 w-3" aria-hidden /> Best
                              </span>
                            ) : null}
                          </div>
                          <div className="text-xs text-gray-500">
                            {row.posts} post{row.posts === 1 ? '' : 's'}{' '}
                            <span className={row.vsAvg >= 0 ? 'text-emerald-600' : 'text-gray-400'}>
                              ({row.vsAvg >= 0 ? '+' : ''}
                              {Math.round(row.vsAvg)}% vs avg)
                            </span>
                          </div>
                        </div>
                        <div className="hidden h-2 w-40 overflow-hidden rounded bg-gray-100 sm:block">
                          <div
                            className={`h-full ${best ? 'bg-red-500' : 'bg-gray-400'}`}
                            style={{ width: `${Math.max(2, (row.er / top) * 100)}%` }}
                          />
                        </div>
                        <span className="w-14 text-right text-sm font-semibold tabular-nums text-gray-900">
                          {row.er.toFixed(1)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Engagement rate per format, pooled like the platform table. Text = a post the
                  vendor gave no media for.
                  {formats.unknown > 0
                    ? ` ${formats.unknown} older post${formats.unknown === 1 ? '' : 's'} synced before formats were recorded ${formats.unknown === 1 ? 'is' : 'are'} left out.`
                    : ''}
                </p>
              </>
            ) : (
              <EmptyState label="No formats yet. Posts synced before media types were recorded have none; the next owned sync fills them in." />
            )}
          </Panel>
        </>
      )}
        </>
      )}
    </>
  );

  return body;
};

export default PostingInsightsPage;
